import { Close, FullscreenOutlined } from '@mui/icons-material';
import { AppBar, Button, Dialog, IconButton, Slide, Toolbar } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import type { Item } from '@/models';
import { FuelPortalService } from '@/services';
import { AssetForm } from '@/store/forms';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';
import { cliptoolUtils } from '@/utils/cliptoolUtils';

import SimplePlayer from '../simple-player/SimplePlayer';
import { toSaveAssetPayload } from './toSaveAssetPayload';

interface Props {
  type: 'video' | 'image';
  videoId?: string;
}

const AI_GENERATION_STEPS = ['Generating Title...', 'Generating Description...', 'Generating Tags...'];

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Preview = ({ type, videoId = 'player1' }: Props) => {
  const { setProgress } = useProgress();
  const { alert } = useAppUtils();
  const [isImageFullScreen, setIsImageFullScreen] = useState(false);
  const [currentTime, setCurrentTime] = useState<Number>(0);
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);

  const { watch, setValue, getValues, reset } = useFormContext();

  const schemaType = watch(`schemaType`);
  const thumbnailUrl = watch('originalAssetData.data.thumbnail.previewUri');
  const assetSrc = watch('assetSrc');
  const markersForPlayer = watch('markersForPlayer');

  const toggleEnlargedThumbnail = () => {
    setIsImageFullScreen(true);
  };

  const onSetThumbnail = () => {
    if (+currentTime >= 0) {
      const formData = getValues();
      if (formData && formData.originalAssetData && formData.originalAssetData.data) {
        formData.originalAssetData.data.thumbnailDelay = currentTime;
      }
      if (
        formData &&
        formData.originalAssetData &&
        formData.originalAssetData.data &&
        formData.originalAssetData.data.thumbnailDelay >= 0
      ) {
        setProgress(true, 'Saving Asset...');
        const payload: Item = toSaveAssetPayload(
          formData as AssetForm,
          ouData.schemas,
          ouData.categories,
          fuelUserAccessData,
          ouData
        );
        const url = cliptoolUtils.getClipThumbnail('player1', 'thumbnailCanvas');
        FuelPortalService.GenerateThumbnailRequestForAsset(payload, url).then((res) => {
          if (res) {
            setProgress(false);
            alert('Operation Successful! It may take a few minutes for the thumbnail on the clip to be updated.');

            // optimistic update
            const imgBlob = cliptoolUtils.getClipThumbnail('player1');
            setValue('originalAssetData.data.thumbnail.tempThumbnailBlob', imgBlob);
          }
        });
      }
    }
  };

  const analyzeWithAI = async () => {
    const formData = getValues();
    let _progressError = false;
    // console.log('onTitleClick', assetForm);
    if (formData && formData.originalAssetData && formData.originalAssetData.id) {
      const clip = formData.originalAssetData;
      try {
        // makes it run simultaneously
        await Promise.all([
          // do the work
          (async function gen() {
            const response = await FuelPortalService.autoFillClip(clip);
            if (response && response.updatedClip) {
              const newFormData: AssetForm = await AssetForm.createFromData(response.updatedClip, permissions, null);
              reset(newFormData);
            }
          })(),
          // simulate progress (6 seconds)
          (async function prog() {
            for (let i = 0; i < AI_GENERATION_STEPS.length; i += 1) {
              if (!_progressError) {
                setProgress(1 + (100 / AI_GENERATION_STEPS.length) * i, AI_GENERATION_STEPS[i]);
                await new Promise((resolve) => setTimeout(resolve, 6000 / AI_GENERATION_STEPS.length));
              }
            }
          })(),
        ]);
      } catch (e) {
        _progressError = true;
        alert('Could not auto-generate metadata for the clip!', 'warning');
        // console.error('Could not auto-generate metadata for the clip!', e);
      } finally {
        setProgress(false);
      }
    }
  };

  return type ? (
    <div>
      <div className="player-container">
        {(schemaType === SchemaType.Video || schemaType === SchemaType.Graphics) && assetSrc && (
          <div className="asset-player">
            <SimplePlayer
              videoId={videoId}
              srcUrl={assetSrc}
              onTimeUpdate={(time: number) => setCurrentTime(time)}
              markers={markersForPlayer}
            />
            <div className="btn-actions">
              {schemaType === SchemaType.Video && (
                <Button variant="contained" color="success" size="small" onClick={onSetThumbnail}>
                  Set Thumbnail
                </Button>
              )}
              {schemaType === SchemaType.Video && (
                <Button variant="contained" color="success" size="small" onClick={analyzeWithAI}>
                  GENERATE WITH AI
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      {schemaType === SchemaType.Image && (
        <div className="image-thumbnail-container">
          <img
            className="image-thumbnail"
            src={thumbnailUrl}
            onClick={toggleEnlargedThumbnail}
            title="CLICK TO ENLARGE"
            alt="asset"
          />
          <FullscreenOutlined className="fullscreen-icon" fontSize="large" />
          {isImageFullScreen && (
            <Dialog
              className="fullscreen-image-popup"
              fullScreen
              open
              onClose={() => setIsImageFullScreen(false)}
              TransitionComponent={Transition}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={() => setIsImageFullScreen(false)} aria-label="close">
                    <Close />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <div className="image-thumbnail-container">
                <img className="image-thumbnail" src={thumbnailUrl} alt="Thumbnail" />
              </div>
            </Dialog>
          )}
        </div>
      )}
    </div>
  ) : null;
};

export default Preview;
